import type { CarProfile } from '../cars/CarProfile';
import type { DrivetrainEvent, DrivetrainState } from '../drivetrain/VirtualDrivetrain';
import { loadCarAudio, type LoadReport } from './EngineSampleManager';
import { RPMMixer } from './RPMMixer';
import { ThrottleProcessor } from './ThrottleProcessor';
import { GearShiftProcessor } from './GearShiftProcessor';
import { EnvironmentProcessor } from './EnvironmentProcessor';
import { SynthVoice } from './synth/SynthVoice';
import { SweepVoice } from './SweepVoice';
import { isCarBrowser } from '../utils/platform';

/**
 * AudioEngine — owns the AudioContext and the signal graph:
 *
 *   RPMMixer (samples) or SynthVoice (worklet) → ThrottleProcessor → engineGain ─┐
 *   GearShiftProcessor (one-shots) ────────────┼→ master → compressor → out
 *   EnvironmentProcessor (wind, ambience) ─────┘
 *
 * It consumes DrivetrainState + events only; it knows nothing about Tesla,
 * GPS or mocks. `start()` must be called from a user gesture.
 */

export type AudioEngineState = 'off' | 'starting' | 'running' | 'suspended' | 'error';

export interface AudioStatus {
  state: AudioEngineState;
  sampleRate: number | null;
  baseLatencyMs: number | null;
  outputLatencyMs: number | null;
  report: LoadReport | null;
  progress: { done: number; total: number } | null;
  error: string | null;
  volume: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private mixer: RPMMixer | null = null;
  private overlayMixer: RPMMixer | null = null;
  private baseGainNode: GainNode | null = null;
  private overlayGainNode: GainNode | null = null;
  private overlayUserGain = 0;
  // the SIMULATOR'S OWN voice: the stock Assetto Corsa bank, so the mod can be judged against what it
  // replaced rather than against silence (Marc: "volumen del simu tal cual"). Testing cars only.
  private simMixer: RPMMixer | null = null;
  private simGainNode: GainNode | null = null;
  private simUserGain = 0;
  // a fourth position from the same mod: engine bay, exhaust, far or close depending on the car
  private extraMixer: RPMMixer | null = null;
  private extraGainNode: GainNode | null = null;
  private extraUserGain = 0;
  // TURBO. Assetto plays the wastegate continuously off boost, not as a one-shot, so it needs a bed of
  // its own rather than a slot in the gearshift processor. Boost is inferred from throttle against
  // revs and SPOOLS: it takes about half a second to come up and a fifth of one to fall away, which is
  // what makes a turbo sound like a turbo instead of a volume knob tied to the pedal.
  private turboBed: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private boost = 0;
  private baseUserGain = 1;      // the AMS2 slider value the user set
  private launchBoostOn = false; // launch control: AMS2 slider pushed to max, restored on release
  private synth: SynthVoice | null = null;
  private sweep: SweepVoice | null = null;
  private sweepGain: GainNode | null = null;
  private idleBed: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  /** the two seams either side of idle: leaving it and settling back into it */
  private moveShots: { away: AudioBuffer | null; settle: AudioBuffer | null; bus: GainNode } | null = null;
  private rolling = false;
  private moveNextAt = 0;
  /** the crack when the throttle opens and the chuff when it shuts, one set per microphone */
  private pedalShots: {
    tip: { buffer: AudioBuffer; recordedRpm: number }[]; lift: { buffer: AudioBuffer; recordedRpm: number }[];
    tipCab: { buffer: AudioBuffer; recordedRpm: number }[]; liftCab: { buffer: AudioBuffer; recordedRpm: number }[];
    tipX: { buffer: AudioBuffer; recordedRpm: number }[]; liftX: { buffer: AudioBuffer; recordedRpm: number }[];
    bus: AudioNode; cabBus: AudioNode | null; xBus: AudioNode | null;
  } | null = null;
  private pedal = 0;
  private pedalNextAt = 0;
  private voiceDuck: GainNode | null = null;
  private bedLoad = 0;
  private atIdle = false;
  private atIdleExitT = 0;
  private atIdleT = 0;   // seconds settled at idle — the bed melts down gradually (Marc)
  private atRest = true;   // the settle clip belongs to coming to a STOP, not to crossing a speed
  private moveMatch = 1;   // the move clips' recorded level against the ladder's (measured at load)
  // NOTE — there is no pull-away CLIP any more, and there must not be one again. That recording is an rpm
  // RISE: played on a clock it runs on while the driver stops half way (Marc: 'jamás puedes darle al play,
  // esa subida tiene que detenerse en las vueltas en las que estoy'). Its material lives in the sample bank
  // instead, as rpm-labelled rungs, so the mixer holds whatever revs the car is actually at.
  private lowStack: { src: AudioBufferSourceNode; gain: GainNode; rpm: number }[] = [];
  private rpmSmooth = 0;
  private rpmAudio = 0;
  private loadHeld = 0;
  private loadSlewed = 0;
  private thrSlewed = 0;
  private postShiftHold = 0;
  private throttle: ThrottleProcessor | null = null;
  private shifts: GearShiftProcessor | null = null;
  private env: EnvironmentProcessor | null = null;
  private master: GainNode | null = null;
  private comp: DynamicsCompressorNode | null = null;
  private engineGain: GainNode | null = null;
  private shiftCut: GainNode | null = null;
  private shiftLP: BiquadFilterNode | null = null;
  private rpmTilt: GainNode | null = null;
  private halfOsc: OscillatorNode | null = null;
  private halfDepth: GainNode | null = null;
  private fullOsc: OscillatorNode | null = null;
  private fullDepth: GainNode | null = null;
  private engineRunning = false;
  private volume = 0.8;
  private muted = false;
  private grantToken = '';
  private demoKey = '';   // DEMO grant → the audio arrives encrypted with this per-token key (decrypted in memory only)
  private status: AudioStatus = {
    state: 'off',
    sampleRate: null,
    baseLatencyMs: null,
    outputLatencyMs: null,
    report: null,
    progress: null,
    error: null,
    volume: 0.8,
  };

  constructor(private readonly profile: CarProfile) {}

  /** Entitlement token for sample downloads (set before start()). */
  setGrantToken(t: string, demoKey = ''): void {
    this.grantToken = t;
    this.demoKey = demoKey;
  }

  getStatus(): AudioStatus {
    if (this.ctx) {
      this.status.sampleRate = this.ctx.sampleRate;
      this.status.baseLatencyMs = Math.round((this.ctx.baseLatency ?? 0) * 1000 * 10) / 10;
      const out = (this.ctx as AudioContext & { outputLatency?: number }).outputLatency;
      this.status.outputLatencyMs = out != null ? Math.round(out * 1000 * 10) / 10 : null;
      if (this.status.state === 'running' && this.ctx.state !== 'running') this.status.state = 'suspended';
      if (this.status.state === 'suspended' && this.ctx.state === 'running') this.status.state = 'running';
    }
    return { ...this.status };
  }

  /** Must be invoked synchronously inside a user gesture (click/touch). */
  async start(): Promise<void> {
    if (this.ctx) {
      await this.resume();
      return;
    }
    try {
      this.status.state = 'starting';
      // 'interactive' = the smallest buffer the device offers: tight sync with the HUD (Marc: 'el audio
      // va tarde'), and on the Mac the worklet glitch counter read 0, so bigger buffers were not needed
      // THERE. The car's own browser is another machine: Marc heard 'piedrecitas' in the Tesla, a fast,
      // sharp crackle like a bad recording, on the three Italians (57 looping sources) and not on the
      // Porsche (26), and never on the Mac. That is the audio thread missing render quanta (see the
      // note in RPMMixer.applySet: a missed quantum is a click, a comb at multiples of 375 Hz). With
      // GPS at 0.3-0.5 Hz on that screen the extra tens of milliseconds of a 'playback' buffer are
      // nothing, and the car browser gets the buffer it needs instead of the one the HUD would like.
      // 48 kHz, ALWAYS, whatever the device runs at. Every bounce is 48 kHz, and its rungs are
      // regions laid end to end; a context at 44.1 kHz makes decodeAudioData resample the whole file,
      // and the resampler's kernel smears each region's first and last samples with the neighbouring
      // region's content, which is another rpm entirely. Measured on the Aventador's idle rung: the
      // seam's high-frequency energy goes from 0.9 to 10.5 times the region's own maximum after a
      // 48k-to-44.1k resample, and every wrap of every live source is then a click (Marc, in the
      // Tesla: 'todo lo que acaba y vuelve a empezar suena ese click'). At 48 kHz nothing is
      // resampled at decode; the browser resamples the finished mix to the device instead, once,
      // where there are no seams to smear.
      const hint: AudioContextLatencyCategory = isCarBrowser() ? 'playback' : 'interactive';
      let ctx: AudioContext;
      try { ctx = new AudioContext({ latencyHint: hint, sampleRate: 48000 }); }
      catch { ctx = new AudioContext({ latencyHint: hint }); }
      this.ctx = ctx;
      void ctx.resume();
      this.watchRenderLoad(ctx);

      // graph
      const master = ctx.createGain();
      master.gain.value = this.muted ? 0 : this.volume * this.profile.audio.masterGain;
      const comp = ctx.createDynamicsCompressor();
      // master went from 0.9 to 1.15 on every car (Marc, from the Tesla: 'no está suficientemente alto'):
      // the threshold comes down and the ratio up by the same margin, so the peaks stay where they were
      comp.threshold.value = -12;
      comp.knee.value = 12;
      comp.ratio.value = 4;
      comp.attack.value = 0.004;
      comp.release.value = 0.18;
      master.connect(comp).connect(ctx.destination);
      this.master = master;
      this.comp = comp;

      const loaded = await loadCarAudio(ctx, this.profile, (done, total) => {
        this.status.progress = { done, total };
      }, this.grantToken, this.demoKey, (e) => {
        // the pit box's atmosphere, arriving behind the engine: wire it in wherever the driver is by then
        if (!this.ctx || !this.master) return;
        try {
          const late = new EnvironmentProcessor(this.ctx, e.wind, e.ambience, e.pitLife, e.debris, { music: e.music, story: e.story });
          late.output.connect(this.master);
          late.start();
          this.env?.stop();
          this.env = late;
        } catch { /* the engine is already running: an atmosphere that fails to wire in changes nothing */ }
      });
      this.status.report = loaded.report;
      if (loaded.report.denied) {
        // no recordings arrived: refuse to run rather than sing through the synthesiser
        this.status.state = 'error';
        this.status.error = 'audio-denied';
        try { void ctx.close(); } catch { /* ignore */ }
        this.ctx = null;
        return;
      }

      // the rungs play in our own sampler wherever worklets exist (see RPMMixerOptions.sampler)
      const sampler = await RPMMixer.prepare(ctx);
      const throttle = new ThrottleProcessor(ctx, this.profile.audio);
      throttle.cleanVoice = this.profile.audio.voice === 'clean';
      const shifts = new GearShiftProcessor(ctx, loaded.oneShots);
      // built empty on purpose: the real wind/ambience arrive through the callback above, a moment later
      const env = new EnvironmentProcessor(ctx, loaded.oneShots.wind, loaded.oneShots.ambience, loaded.oneShots.pitLife, loaded.oneShots.debris, { music: loaded.oneShots.music, story: loaded.oneShots.story });

      const engineGain = ctx.createGain();
      engineGain.gain.value = this.engineRunning ? 1 : 0; // ENG START opens it
      this.engineGain = engineGain;
      const shiftCut = ctx.createGain(); // momentary ignition cut on gearchanges
      shiftCut.gain.value = 1;
      this.shiftCut = shiftCut;
      const shiftLP = ctx.createBiquadFilter(); // the cut goes DARK (combustion off: only rumble) — measured on the real pull
      shiftLP.type = 'lowpass'; shiftLP.frequency.value = 20000; shiftLP.Q.value = 0.4;
      this.shiftLP = shiftLP;
      // car-body voicing: the reference the user hears against (Marc's Portimao lap video) is a car heard
      // from OUTSIDE/roof — our licensed material is a bright close mic. One gentle shelf brings the voice
      // into that world (lap compare: real centroid ~294 Hz vs ours 965 before voicing).
      const voicing = ctx.createBiquadFilter();
      // 'clean' cars skip the shelf entirely: an in-game interior sample is already the finished voice
      voicing.type = 'highshelf'; voicing.frequency.value = 1800;
      voicing.gain.value = this.profile.audio.voice === 'clean' ? 0 : this.profile.audio.voicingDb ?? -5;
      const rpmTilt = ctx.createGain(); // a real cabin gets louder with revs (+5 dB idle → limiter)
      rpmTilt.gain.value = 0.7;
      this.rpmTilt = rpmTilt;
      // cycle-to-cycle "vibration": amplitude modulation at the half order (rpm/120) and the first order (rpm/60)
      // puts the half-order sidebands on every harmonic — measured on the real cabin pull: ~1/3 of the line energy
      const halfOsc = ctx.createOscillator(); halfOsc.type = 'sine'; halfOsc.frequency.value = 12;
      const halfDepth = ctx.createGain(); halfDepth.gain.value = 0;
      halfOsc.connect(halfDepth).connect(rpmTilt.gain); halfOsc.start();
      const fullOsc = ctx.createOscillator(); fullOsc.type = 'sine'; fullOsc.frequency.value = 24;
      const fullDepth = ctx.createGain(); fullDepth.gain.value = 0;
      fullOsc.connect(fullDepth).connect(rpmTilt.gain); fullOsc.start();
      this.halfOsc = halfOsc; this.halfDepth = halfDepth; this.fullOsc = fullOsc; this.fullDepth = fullDepth;
      const synthSpec = this.profile.samples.synth;
      if (loaded.sweeps.length && SweepVoice.supported()) {
        // granular sweep player: long untouched real passages scrubbed by rpm (the real dynamics come with them)
        const sweep = new SweepVoice(ctx, loaded.sweeps);
        sweep.lowLvlRef = (loaded.report as { lowLvlRef?: { start: number; step: number; db: number[] } | null }).lowLvlRef ?? null;
        await sweep.start();
        const sweepGain = ctx.createGain(); sweepGain.gain.value = 1;
        sweep.output.connect(sweepGain).connect(throttle.input);
        this.sweepGain = sweepGain;
        throttle.neutralGain = true;
        this.sweep = sweep;
        // the DUMB low-speed voice, classic game-engine style: three plain loops at stepped rpm,
        // crossfaded by revs — each plays within ±15 % of its natural pitch, and nothing can glitch.
        const lowMaster = ctx.createGain(); lowMaster.gain.value = 0.93; lowMaster.connect(throttle.input);
        this.lowStack = loaded.oneShots.lowLoops.map((ll) => {
          const src = ctx.createBufferSource();
          src.buffer = ll.buffer;
          src.loop = true;
          const lg = ctx.createGain(); lg.gain.value = 0;
          src.connect(lg).connect(lowMaster);
          src.start(0, Math.random() * Math.max(0, ll.buffer.duration - 1));
          return { src, gain: lg, rpm: ll.rpm };
        });
        loaded.report.format = (loaded.report.format ?? '') + ' sweeps+lowloop';
      } else if (synthSpec && SynthVoice.supported()) {
        // procedural engine (physical model in an AudioWorklet) — no loops at all
        const synth = new SynthVoice(ctx, synthSpec);
        await synth.start();
        synth.output.connect(throttle.input);
        this.synth = synth;
        loaded.report.format = 'synth';
      } else {
        const mixer = new RPMMixer(ctx, { on: loaded.on, off: loaded.off, mid: loaded.oneShots.midLoops }, {
          onMixFloor: this.profile.audio.onMixFloor,
          tau: this.profile.audio.envelopeTauS,
          stopSilent: isCarBrowser(),
          sampler,
        });
        const baseG = ctx.createGain();
        baseG.gain.value = this.profile.samples.baseGain ?? this.profile.samples.overlay?.baseGain ?? 1;
        this.baseUserGain = baseG.gain.value;
        this.baseGainNode = baseG;
        mixer.output.connect(baseG).connect(throttle.input);
        mixer.start();
        this.mixer = mixer;
      }
      if (loaded.oneShots.overlayOn.length) {
        // OVERLAY voice (MIX experiments): a second rpm-matched ladder layered over the main voice
        const ov = new RPMMixer(ctx, { on: loaded.oneShots.overlayOn, off: loaded.oneShots.overlayOff.length ? loaded.oneShots.overlayOff : loaded.oneShots.overlayOn, mid: loaded.oneShots.overlayMid }, {
          onMixFloor: this.profile.audio.onMixFloor,
          tau: this.profile.audio.envelopeTauS,
          stopSilent: isCarBrowser(),
          sampler,
        });
        const og = ctx.createGain(); og.gain.value = 0;
        this.overlayGainNode = og;
        // the cabin copy of the launch bed rides this fader too
        shifts.setCabinBus(og);
        this.overlayUserGain = this.profile.samples.overlay?.gain ?? 0.35;
        ov.output.connect(og).connect(throttle.input);
        ov.start();
        this.overlayMixer = ov;
      }
      if (loaded.oneShots.simOn.length) {
        const sm = new RPMMixer(ctx, { on: loaded.oneShots.simOn, off: loaded.oneShots.simOff.length ? loaded.oneShots.simOff : loaded.oneShots.simOn }, {
          onMixFloor: this.profile.audio.onMixFloor,
          tau: this.profile.audio.envelopeTauS,
          stopSilent: isCarBrowser(),
          sampler,
        });
        const sg = ctx.createGain(); sg.gain.value = 0;
        this.simGainNode = sg;
        this.simUserGain = this.profile.samples.sim?.gain ?? 0;
        sm.output.connect(sg).connect(throttle.input);
        sm.start();
        this.simMixer = sm;
      }
      if (loaded.oneShots.extraOn.length) {
        const xm = new RPMMixer(ctx, { on: loaded.oneShots.extraOn, off: loaded.oneShots.extraOff.length ? loaded.oneShots.extraOff : loaded.oneShots.extraOn, mid: loaded.oneShots.extraMid }, {
          onMixFloor: this.profile.audio.onMixFloor,
          tau: this.profile.audio.envelopeTauS,
          stopSilent: isCarBrowser(),
          sampler,
        });
        const xg = ctx.createGain(); xg.gain.value = 0;
        this.extraGainNode = xg;
        shifts.setExtraBus(xg);
        this.extraUserGain = this.profile.samples.extra?.gain ?? 0;
        xm.output.connect(xg).connect(throttle.input);
        xm.start();
        this.extraMixer = xm;
      }
      if (loaded.oneShots.tipIn.length || loaded.oneShots.liftOff.length) {
        const pb = ctx.createGain(); pb.gain.value = 1; pb.connect(master);
        this.pedalShots = {
          tip: loaded.oneShots.tipIn, lift: loaded.oneShots.liftOff,
          tipCab: loaded.oneShots.tipInCabin, liftCab: loaded.oneShots.liftOffCabin,
          tipX: loaded.oneShots.tipInExtra, liftX: loaded.oneShots.liftOffExtra,
          // read the overlay bus HERE rather than assigning it where the overlay is built: this block
          // runs after that one, so a hand-off in the other direction leaves the cabin set silent
          bus: pb, cabBus: this.overlayGainNode, xBus: this.extraGainNode,
        };
      }
      if (loaded.oneShots.pullaway || loaded.oneShots.settle) {
        const mb = ctx.createGain(); mb.gain.value = 1; mb.connect(master);
        // LEVEL-MATCHED TO THE LADDER IT RIDES ON. These clips go straight to the master while the
        // voice passes through rpmTilt, and on the SVJ they are recorded 2 dB hotter than the rungs
        // as well - so at a crawl, where the tilt is 0.55, the clip landed some 5 dB ABOVE the engine
        // it is supposed to sit on top of. Measured on a gentle pull-away: 7-8 dB over the mix for
        // the two seconds it plays, which is Marc's "de repente sube el volumen sin venir a cuento".
        // Measured here rather than guessed: the clip's own RMS against the idle rung's.
        const rmsOf = (b: AudioBuffer | null) => {
          if (!b) return 0;
          const d = b.getChannelData(0);
          let acc = 0, n = 0;
          for (let i = 0; i < d.length; i += 7) { acc += d[i] * d[i]; n++; }
          return n ? Math.sqrt(acc / n) : 0;
        };
        const ref = rmsOf(loaded.on[0]?.buffer ?? null);
        const clip = Math.max(rmsOf(loaded.oneShots.pullaway), rmsOf(loaded.oneShots.settle));
        this.moveMatch = ref > 0 && clip > 0 ? Math.max(0.3, Math.min(1.5, ref / clip)) : 1;
        this.moveShots = { away: loaded.oneShots.pullaway, settle: loaded.oneShots.settle, bus: mb };
      }
      if (loaded.oneShots.turbo) {
        const tsrc = ctx.createBufferSource(); tsrc.buffer = loaded.oneShots.turbo; tsrc.loop = true;
        const tg = ctx.createGain(); tg.gain.value = 0;
        tsrc.connect(tg).connect(master);
        tsrc.start(0, Math.random() * Math.max(0, loaded.oneShots.turbo.duration - 2));
        this.turboBed = { src: tsrc, gain: tg };
      }
      const voiceDuck = ctx.createGain(); voiceDuck.gain.value = 1;   // ducks the engine VOICE while the real idle bed plays (any voice path: sweeps or mixer)
      this.voiceDuck = voiceDuck;
      throttle.output.connect(shiftCut).connect(shiftLP).connect(voicing).connect(rpmTilt).connect(voiceDuck).connect(engineGain).connect(master);
      if (loaded.oneShots.idleBed) {
        // parked idle = the REAL recording tal cual — a plain looping source straight into the engine
        // bus (no throttle shaping, no voicing), for BOTH voice paths (sweeps and RSR mixer)
        const ibSrc = ctx.createBufferSource(); ibSrc.buffer = loaded.oneShots.idleBed; ibSrc.loop = true;
        const ibGain = ctx.createGain(); ibGain.gain.value = 0;
        ibSrc.connect(ibGain).connect(engineGain);
        ibSrc.start(0, Math.random() * Math.max(0, loaded.oneShots.idleBed.duration - 2));
        this.idleBed = { src: ibSrc, gain: ibGain };
      }
      shifts.output.connect(master);
      env.output.connect(master);

      env.start();

      this.throttle = throttle;
      this.shifts = shifts;
      this.env = env;
      this.status.state = ctx.state === 'running' ? 'running' : 'suspended';
      ctx.onstatechange = () => {
        if (this.status.state === 'off' || this.status.state === 'error') return;
        this.status.state = ctx.state === 'running' ? 'running' : 'suspended';
      };
    } catch (err) {
      this.status.state = 'error';
      this.status.error = err instanceof Error ? err.message : String(err);
    }
  }

  async resume(): Promise<void> {
    if (!this.ctx) return;
    try {
      if (this.ctx.state !== 'running') await this.ctx.resume();
    } catch {
      /* ignore */
    }
  }

  /** a line every ten seconds about the audio clock against the wall clock: the underrun meter */
  onHealth: ((line: string) => void) | null = null;
  private healthWall0 = 0;
  private healthCtx0 = 0;
  private healthNextAt = 0;
  private healthDriftPrev = 0;
  /** the browser's own account of its audio thread (AudioContext.renderCapacity, Chromium 123+) */
  private renderLoad: { avg: number; peak: number; underrun: number } | null = null;

  /**
   * THE REAL UNDERRUN METER. The wall-clock drift above lies on the car: the Porsche drifts +52 s
   * and is clean, the Italians +262 s and crackle, so drift is something systematic in that
   * browser's audio clock and says nothing about missed quanta. renderCapacity is the render
   * thread's own tally: how much of each quantum's budget it used, and the share of quanta it
   * could not finish in time (underrunRatio). That last number IS the crackle, when it is the
   * browser's. Older builds have no such thing and the line says so.
   */
  private watchRenderLoad(ctx: AudioContext): void {
    type Cap = { start(o?: { updateInterval?: number }): void; addEventListener(t: 'update', f: (e: { averageLoad: number; peakLoad: number; underrunRatio: number }) => void): void };
    const cap = (ctx as AudioContext & { renderCapacity?: Cap }).renderCapacity;
    if (!cap || typeof cap.start !== 'function') return;
    try {
      cap.addEventListener('update', (e) => { this.renderLoad = { avg: e.averageLoad, peak: e.peakLoad, underrun: e.underrunRatio }; });
      cap.start({ updateInterval: 1 });
    } catch { /* not there: the line will say n/a */ }
  }

  update(state: DrivetrainState, dtS: number): void {
    if (!this.throttle || !this.env) return;
    // THE UNDERRUN METER. A render thread that misses quanta stalls the context's clock while the
    // wall clock keeps going, so the drift between the two, taken every ten seconds, is the number of
    // seconds of audio the device did NOT get. Zero means the crackle is not the browser's.
    if (this.ctx && this.onHealth) {
      const wall = performance.now() / 1000, ct = this.ctx.currentTime;
      if (!this.healthWall0) { this.healthWall0 = wall; this.healthCtx0 = ct; this.healthNextAt = wall + 10; }
      else if (wall >= this.healthNextAt) {
        const drift = (wall - this.healthWall0) - (ct - this.healthCtx0);
        const step = drift - this.healthDriftPrev;
        this.healthDriftPrev = drift;
        this.healthNextAt = wall + 10;
        this.onHealth(`audio health: drift ${drift >= 0 ? '+' : ''}${(drift * 1000).toFixed(0)}ms (${step >= 0 ? '+' : ''}${(step * 1000).toFixed(0)}ms/10s) ${this.contextInfo} live${this.mixer?.liveCount ?? '?'} run${this.mixer?.runningCount ?? '?'}`);
      }
    }
    this.mixer?.update(state.rpm, state.throttle);
    this.overlayMixer?.update(state.rpm, state.throttle);
    this.simMixer?.update(state.rpm, state.throttle);
    if (this.simGainNode && this.ctx) this.simGainNode.gain.setTargetAtTime(this.simUserGain, this.ctx.currentTime, 0.08);
    this.extraMixer?.update(state.rpm, state.throttle);
    if (this.extraGainNode && this.ctx) this.extraGainNode.gain.setTargetAtTime(this.extraUserGain, this.ctx.currentTime, 0.08);
    if (this.overlayGainNode && this.ctx) {
      // the overlay ladder has no idle material: near idle / at the start flare / crawling it served
      // its 2000-rpm GAS loop as a background drone (Marc: 'ruido raro 2 s al arrancar y de 0 a 11')
      // Marc (2026-08-25): the drone had only moved to 13-16 km/h (where the gate opened at 2100-2500 rpm) — the
      // real ladder's bottom loop has nothing musical there: keep the overlay out until ~1.5x idle and ~20 km/h
      // the overlay ladder has no usable idle material: near idle / crawling it served its 2000-rpm GAS
      // loop as a background drone (Marc: 'ruido raro de 0 a 11'), so it stays out until the car is
      // properly moving. Filling the bottom of the ladder is what would let this open earlier, and every
      // attempt at that from the Run_08 take came back as a siren — it needs new material, not new maths.
      // A ladder that owns an idle does not need any of this: it plays from tickover like the base does.
      const gate = this.profile.samples.overlay?.gate === 'none'
        ? 1
        : Math.min(1, Math.max(0, (state.speedKmh - 14) / 10)) * Math.min(1, Math.max(0, (state.rpm - 2900) / 700));
      this.overlayGainNode.gain.setTargetAtTime(this.overlayUserGain * gate, this.ctx.currentTime, 0.08);
    }
    this.synth?.update(state.rpm, state.throttle);
    if (this.sweep) {
      // ON↔OFF blend: a light pedal is still "on load" in the real cabin (on_a was recorded at ~35 %); only a
      // closed throttle is overrun → full ON by ~28 % pedal. During a gearchange hold the blend: the real shift is
      // a cut INSIDE the same recording (clip + duck carry it), not a swap to the overrun recording and back.
      const intent = state.shifting || this.postShiftHold > 0 ? this.loadHeld : Math.min(1, Math.max(0, (state.throttleIntent - 0.03) / 0.18));
      if (!state.shifting && this.postShiftHold <= 0) this.loadHeld = intent;
      // the AUDIBLE load is a foot's view: at low rpm a 4 Hz key-tap wiggle must not swap the
      // ON/OFF recordings per tap (Marc's alto-bajo holding 16 km/h) — slew it; full bite above 3.2k
      this.loadSlewed += (intent - this.loadSlewed) * Math.min(1, dtS / (state.rpm < 3200 && !state.shifting ? 0.22 : 0.04));
      this.thrSlewed += (state.throttle - this.thrSlewed) * Math.min(1, dtS / (state.rpm < 3200 && !state.shifting ? 0.22 : 0.03));
      this.postShiftHold = state.shifting ? 0.12 : Math.max(0, this.postShiftHold - dtS);
      // lugging = rolling in gear at very low revs (clutch locked, engine dragged / labouring): unlocks the near-stall
      // recordings for the OFF side. Measured on Marc's reference: the loud bright shudder lives at 1.2–2.4 k in 1st
      // with the pedal OFF (clutch engaged, engine braking). A gentle pull-away on a light pedal is on_a, not lugging.
      // lugging = the revs GENUINELY dragged under idle. It must NEVER fire just because the pedal is off
      // at 1.4–1.9k — that was the 'se ahoga' shudder pumping between Marc's keyboard taps at 13–18 km/h.
      // the choke/sag shudder only when the revs are DEEP under idle (<80 %) — at idle 1700 the 0.97 gate
      // covered half the coasting range and the choke sneaked in 'randomly' in 2nd
      // Marc: 1–18 km/h must be SPOTLESS — no choke ever in 1st. The drag shudder lives in 2nd+ only
      // (his original 'segunda a paso de persona').
      const lug = state.engine === 'on' && !state.clutchSlip && state.gear >= 2 && state.speedKmh > 2 && state.rpm < this.profile.idleRpm * 0.8;
      // clutch open/slipping (pull-away, braked stop, start-up flare): light-load low revs are the IDLE
      // recording pitched up — the real creep sound — never a wobble between idle and driving textures
      // 'slip' now means TRUE STANDSTILL: the idle lock may only hold while parked. Creeping (clutch
      // slipping at 5-18 km/h) lives entirely in the low bed — flapping idle↔bed on longer pedal taps
      // was the chirp train in Marc's video (16 km/h, 1st, COAST, no gearchanges).
      // idle-lock bias fades over 2→8 km/h instead of flipping at 2 (the audible idle→moving seam, Marc)
      const slip = state.engine === 'on' ? Math.max(0, Math.min(1, 1 - (state.speedKmh - 2) / 6)) : 0;
      const jitter = state.gear === 2 && state.speedKmh < 45;   // Marc: micro-jitter only there
      // jank-proof rpm: main-thread hiccups deliver rpm in steps — slew it here so the voices only ever
      // see a smooth curve regardless of frame timing (Marc records his screen while testing)
      if (this.rpmSmooth <= 0) this.rpmSmooth = state.rpm;
      const maxStep = 4200 * dtS;
      this.rpmSmooth += Math.max(-maxStep, Math.min(maxStep, state.rpm - this.rpmSmooth));
      // telemetry arrives in STEPS when the tab is loaded (timers coalesce to ~4 Hz bursts) — and in
      // 1st gear at 16 km/h a ±0.3 km/h step is ±42 rpm 4×/s: an audible alto-bajo (Marc's REC).
      // A wheel-locked engine cannot step — give the AUDIO rpm real inertia at low revs; free-rev,
      // gearchanges and high revs stay immediate.
      if (this.rpmAudio <= 0) this.rpmAudio = this.rpmSmooth;
      const lpTau = state.speedKmh > 2 && !state.shifting && this.rpmSmooth < 3200 ? 0.15 : 0.02;
      this.rpmAudio += (this.rpmSmooth - this.rpmAudio) * Math.min(1, dtS / lpTau);
      this.sweep.update(this.rpmAudio, this.loadSlewed, lug, slip, jitter);
      if (this.lowStack.length && this.ctx) {
        const nowT = this.ctx.currentTime;
        const driving = state.engine === 'on' && state.speedKmh >= 2;
        const r = this.rpmAudio;
        // the stack owns slow driving; hand over to the granular engine across 2.75–3.15k (≈20 km/h in 1st)
        const lowMix = driving && this.engineRunning ? Math.min(1, Math.max(0, (3150 - r) / 400)) : 0;
        // triangular weights over the stack labels, normalized — a continuous morph, never a 'gearchange'
        const labels = this.lowStack.map((l) => l.rpm);
        const wts = labels.map((L, i) => {
          const lo = i === 0 ? 0 : labels[i - 1];
          const hi = i === labels.length - 1 ? 1e9 : labels[i + 1];
          if (r <= L) return i === 0 ? 1 : Math.max(0, (r - lo) / Math.max(1, L - lo));
          return i === labels.length - 1 ? 1 : Math.max(0, (hi - r) / Math.max(1, hi - L));
        });
        const sum = wts.reduce((a, b2) => a + b2, 0) || 1;
        this.lowStack.forEach((l, i) => {
          const g = Math.sqrt(wts[i] / sum) * lowMix;
          l.gain.gain.setTargetAtTime(g, nowT, 0.12);
          l.src.playbackRate.setTargetAtTime(Math.max(0.7, Math.min(1.45, r / l.rpm)), nowT, 0.09);
        });
        if (this.sweepGain) this.sweepGain.gain.setTargetAtTime(this.atIdle ? 0.03 : 1 - lowMix * 0.96, nowT, 0.12);
      } else if (this.sweepGain && this.ctx) {
        // no loop stack (the low range IS the real passage in the granular voice): only the parked-idle
        // bed ever displaces it
        this.sweepGain.gain.setTargetAtTime(this.atIdle ? 0.03 : 1, this.ctx.currentTime, this.atIdle ? 0.12 : 0.35);
      }
    }

    // REAL IDLE BED gate — runs for BOTH voice paths (granular sweeps and the RSR mixer): parked or
    // creeping at idle with no demand, the licensed real idle recording plays and the voice ducks.
    if (this.idleBed && this.ctx) {
      const idleR = this.profile.idleRpm;
      this.bedLoad += ((state.throttleIntent ?? 0) - this.bedLoad) * Math.min(1, dtS / 0.2);
      const idleFree = state.rpm < idleR * 1.25 && state.speedKmh < 2;   // parked idle ONLY; 1.25x lets the bed enter right after the catch (the start flare holds ~1.15x for seconds — Marc's 3-s late idle)
      this.atIdleExitT = (!idleFree || state.rpm > idleR * 1.35 || this.bedLoad > 0.35 || state.freeRev) ? this.atIdleExitT + dtS : 0;
      if (this.atIdle) { if (this.atIdleExitT > 0.15) this.atIdle = false; }
      else if (idleFree && this.bedLoad < 0.06 && !state.freeRev) this.atIdle = true;
      const on = this.atIdle && this.engineRunning;
      // Marc: the settled idle melts down AND the clip is level-matched to the rest of the mix — it was
      // recorded hotter than the voice ('ese clip suena más que el resto'): enter at 0.6, glide to ~0.4
      // over the first ~5 s standing, hold there. (The 15 km/h speed-mix experiment is reverted — Marc.)
      this.atIdleT = on ? this.atIdleT + dtS : 0;
      const bedTarget = on ? 0.6 - 0.2 * Math.min(1, this.atIdleT / 5) : 0;
      // leaving the bed: a GENTLE roll-off (no real pedal demand) gets a LONG overlapped crossfade — the
      // voice rises underneath the real idle for ~0.7 s so pulling away poco a poco is one continuous
      // sound; a hard pedal stab keeps the snappy handover so the response stays immediate
      const gentleExit = !on && this.bedLoad < 0.2 && !state.freeRev;
      this.idleBed.gain.gain.setTargetAtTime(bedTarget, this.ctx.currentTime, on ? 0.35 : gentleExit ? 0.7 : 0.25);
      // while the real pull-away clip rides, the voice stays low underneath and rises as the clip's baked
      // the real idle bed ducks the synthesised voice while it plays, handing back over ~0.6 s so that
      // pulling away poco a poco stays one continuous sound
      if (this.voiceDuck) this.voiceDuck.gain.setTargetAtTime(on ? 0.04 : 1, this.ctx.currentTime, on ? 0.15 : gentleExit ? 0.6 : 0.3);
    }
    // PEDAL TRANSIENTS. A ladder of steady loops cannot make the moment the throttle opens or shuts,
    // because neither the revs nor the load are steady there, so a crossfade slides over it and the
    // instant simply is not in the sound. These are the bank's own two-hundred-millisecond takes of
    // exactly that, picked by nearest revs and laid over the voice rather than mixed into it.
    if (this.pedalShots && this.ctx && this.engineRunning) {
      const now = this.ctx.currentTime;
      const prev = this.pedal;
      const nowT = state.throttleIntent ?? state.throttle ?? 0;
      this.pedal += (nowT - this.pedal) * Math.min(1, dtS / 0.06);
      const rose = this.pedal - prev, rpm = state.rpm;
      // a real pedal MOVE, not the wobble of an estimated throttle, and never twice on top of itself
      const fire = (set: { buffer: AudioBuffer; recordedRpm: number }[], cab: { buffer: AudioBuffer; recordedRpm: number }[], ext: { buffer: AudioBuffer; recordedRpm: number }[], gain: number) => {
        if (now < this.pedalNextAt) return;
        // one set per microphone, each on its own fader, so all three move together
        for (const [list, dest] of [[set, this.pedalShots!.bus], [cab, this.pedalShots!.cabBus], [ext, this.pedalShots!.xBus]] as const) {
          if (!list.length || !dest) continue;
          let best = list[0];
          for (const l of list) if (Math.abs(l.recordedRpm - rpm) < Math.abs(best.recordedRpm - rpm)) best = l;
          const src = this.ctx!.createBufferSource();
          src.buffer = best.buffer;
          src.playbackRate.value = Math.min(1.18, Math.max(0.85, rpm / (best.recordedRpm || rpm || 1)));
          // in over 5 ms and out over the take's last 30 ms: a two-hundred-millisecond take that starts
          // and ends on whatever sample the cut landed on is two clicks (Marc's Premiere rule)
          const g = this.ctx!.createGain();
          const dur = best.buffer.duration / src.playbackRate.value;
          g.gain.setValueAtTime(0, now);
          g.gain.linearRampToValueAtTime(gain, now + 0.005);
          g.gain.setValueAtTime(gain, now + Math.max(0.006, dur - 0.03));
          g.gain.linearRampToValueAtTime(0, now + dur);
          src.connect(g).connect(dest);
          src.start(now);
          src.onended = () => { try { src.disconnect(); g.disconnect(); } catch { /* noop */ } };
        }
        this.pedalNextAt = now + 0.22;
      };
      // level rises with the revs, the way the backfires already do (0.35 to 0.85 there). A flat gain
      // made a transient louder than a bang at idle, which is the wrong way round.
      const rn = Math.max(0, Math.min(1, state.rpmNorm ?? 0));
      if (rpm > 1200 && rose > 0.045 && this.pedal > 0.35) fire(this.pedalShots.tip, this.pedalShots.tipCab, this.pedalShots.tipX, 0.30 + 0.35 * rn);
      else if (rpm > 1500 && rose < -0.055 && prev > 0.3) fire(this.pedalShots.lift, this.pedalShots.liftCab, this.pedalShots.liftX, 0.32 + 0.38 * rn);
    }

    // THE TWO SEAMS EITHER SIDE OF IDLE. Pulling away and coming back to rest are the two moments a
    // ladder of steady loops cannot make, because there is no steady state to hold: the engine is
    // moving through revs under a changing load. Where the mod recorded them, they ride ON TOP of the
    // voice rather than replacing it, so a missed edge costs nothing and a caught one costs no seam.
    if (this.moveShots && this.ctx && this.engineRunning) {
      const now = this.ctx.currentTime;
      // ONE THRESHOLD IS A FLIP-FLOP (Marc, on the SVJ: "entre 3 y 10km/h suena de repente un audio con
      // mas volumen q otros sin venir a cuento... cuando estas en coast sin darle a gas o muy poquito").
      // `rolling` turned on and off at exactly 6 km/h, so trundling across it - which is what a car does
      // at walking pace - fired the pull-away clip, then the settle, then the pull-away again, every few
      // seconds, at 0.85 into the mix.
      // These are not one event with two directions, they are TWO events, and each has its own test:
      //   the pull-away is the engine taking up load, so it needs the car to start moving AND the driver
      //   to be on the pedal - coasting through walking pace with a shut throttle is not a move-off;
      //   the settle is the engine coming back to idle, so it needs the car to actually STOP, not merely
      //   to drop under some threshold while still rolling.
      // Measured on a crawl that oscillates 3-10 km/h with the throttle shut: one threshold gave a
      // 9 dB burst every 5.5 seconds; with these two, nothing fires at all, which is right.
      const pedal = state.throttleIntent ?? state.throttle ?? 0;
      const moving = this.rolling
        ? state.speedKmh > 4 || state.rpm > this.profile.idleRpm * 1.45
        : state.speedKmh > 9 || state.rpm > this.profile.idleRpm * 1.7;
      const atRest = state.speedKmh < 2;
      const fireMove = (buf: AudioBuffer | null) => {
        if (!buf || !this.ctx || now < this.moveNextAt || state.speedKmh >= 28) return;
        // ...and it follows the voice's own rpm tilt, so it arrives where the engine is rather than
        // where the master is: at a crawl that is 0.55, which is exactly the gap that made it jump.
        const tilt = Math.max(0.35, Math.min(1, this.rpmTilt ? this.rpmTilt.gain.value : 1));
        const peak = 0.85 * this.moveMatch * tilt;
        const src = this.ctx.createBufferSource(); src.buffer = buf;
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(peak, now + 0.06);
        g.gain.setValueAtTime(peak, now + Math.max(0.1, buf.duration - 0.35));
        g.gain.linearRampToValueAtTime(0, now + buf.duration);
        src.connect(g).connect(this.moveShots!.bus);
        src.start(now);
        src.onended = () => { try { src.disconnect(); g.disconnect(); } catch { /* noop */ } };
        this.moveNextAt = now + buf.duration + 2.5;
      };
      if (moving && !this.rolling && pedal > 0.08) fireMove(this.moveShots.away);
      if (atRest && !this.atRest) fireMove(this.moveShots.settle);
      this.rolling = moving;
      this.atRest = atRest;
    }
    if (this.sweep && this.rpmTilt && this.ctx) {
      // the real passages carry their own level-vs-rpm, lug loudness and cycle-to-cycle shudder: no tilt, no AM
      this.rpmTilt.gain.setTargetAtTime(1.0, this.ctx.currentTime, 0.08);
    } else if (this.rpmTilt && this.ctx && this.profile.audio.voice === 'clean') {
      // AC-style ladder: only the game's own level-vs-rpm ramp (rungs are normalized flat, the rise has
      // to come from here) — no tremolo, no lug boost, no move-off lift. Those read as WOBBLE on samples
      // that already carry their combustion texture.
      this.rpmTilt.gain.setTargetAtTime(0.55 + 0.45 * Math.max(0, state.rpmNorm), this.ctx.currentTime, 0.08);
      if (this.halfDepth && this.fullDepth) {
        this.halfDepth.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
        this.fullDepth.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
      }
    } else if (this.rpmTilt && this.ctx) {
      const now = this.ctx.currentTime;
      // real cabin: ≈ +1 dB per 1000 rpm under load (measured on the pull); lugging below idle under load is LOUD (+5 dB measured)
      const lugBoost = state.rpm < 1.15 * this.profile.idleRpm && state.throttle > 0.05 && state.speedKmh > 1 ? 1.4 : 1.0;   // was 3.6 (Marc: crawl noise)
      // The real idle bed plays LOUD, and the synthesised voice that replaces it as the car moves off came
      // in underneath: a step down at exactly the moment the driver is listening hardest. Marc's call was
      // 'either drop the idle (which I do not want) or raise the first few km/h', so the voice is lifted
      // over the first stretch and eases back to normal by 25 km/h, where the two are level anyway.
      const moveOffLift = 1 + 0.45 * Math.max(0, 1 - state.speedKmh / 25);
      const base = (0.45 + 0.95 * Math.max(0, state.rpmNorm)) * lugBoost * moveOffLift;
      this.rpmTilt.gain.setTargetAtTime(base, now, 0.08);
      if (this.halfOsc && this.halfDepth && this.fullOsc && this.fullDepth && state.engine === 'on') {
        const f05 = Math.max(5, state.rpm / 120), f1 = Math.max(10, state.rpm / 60);
        this.halfOsc.frequency.setTargetAtTime(f05, now, 0.03);
        this.fullOsc.frequency.setTargetAtTime(f1, now, 0.03);
        // deeper under load (combustion variance), lighter on the overrun
        // measured: the real overrun at 4 k is a clean ladder (no half-orders) while lugging shudders hard
        const lug = state.rpm < 1.15 * this.profile.idleRpm && state.throttle > 0.05 ? 1.2 : 1.0;   // was 2.2 (Marc: crawl noise)
        const load = (0.1 + 0.9 * state.throttle) * lug;
        this.halfDepth.gain.setTargetAtTime(base * 0.22 * load, now, 0.1);
        this.fullDepth.gain.setTargetAtTime(base * 0.10 * load, now, 0.1);
      } else if (this.halfDepth && this.fullDepth) {
        this.halfDepth.gain.setTargetAtTime(0, now, 0.1); this.fullDepth.gain.setTargetAtTime(0, now, 0.1);
      }
    }
    // LAUNCH CONTROL: both pedals down at a standstill. The condition is read here rather than in the
    // drivetrain because it is a way of DRIVING, not a state the gearbox is in.
    this.shifts?.setLaunch(
      state.engine === 'on' && state.speedKmh < 3 && state.brake > 0.25 && state.throttle > 0.5,
      state.rpm,
    );
    if (this.turboBed && this.ctx) {
      const want = state.engine === 'on'
        ? Math.max(0, Math.min(1, state.throttle)) * Math.min(1, Math.max(0, (state.rpm - this.profile.idleRpm) / (this.profile.maxRpm * 0.5)))
        : 0;
      this.boost += (want - this.boost) * Math.min(1, dtS / (want > this.boost ? 0.5 : 0.2));
      this.turboBed.gain.gain.setTargetAtTime(0.5 * this.boost, this.ctx.currentTime, 0.06);
      this.turboBed.src.playbackRate.value = 0.85 + 0.3 * Math.max(0, Math.min(1, state.rpmNorm));
    }
    this.throttle.update(state, dtS);   // raw feel again — the slewed feed dulled the mixer response
    this.env.update(state);
    this.shifts?.update(state, dtS);
  }

  handleEvent(e: DrivetrainEvent, state: DrivetrainState): void {
    this.shifts?.handle(e, state);
    if (this.shiftCut && this.ctx && (e.type === 'shiftUp' || e.type === 'shiftDown')) {
      // sequential gearbox: the ECU cuts ignition for ~70 ms on an upshift (shorter, softer on a downshift)
      const g = this.shiftCut.gain;
      const t = this.ctx.currentTime;
      // measured on the real cabin pull: an upshift is a SHORT dip (−3…−5 dB for ~60–100 ms) during which the
      // tone goes DARK (centroid ~400 Hz: combustion off, only rumble), then a bright re-ignition burst; the level
      // settles ~2 dB lower in the new gear. The voice does exactly that (duck + lowpass); the real clip adds
      // only the mechanical clack / bang (high-passed, no engine tone → nothing to phase against).
      if (e.type === 'shiftDown') {
        // the lap's downshifts: a gentle +3..+5 dB blip bump over the quiet overrun for ~300 ms
        g.cancelScheduledValues(t);
        g.setValueAtTime(1, t);
        g.linearRampToValueAtTime(1.32, t + 0.06);
        g.setValueAtTime(1.32, t + 0.2);
        g.linearRampToValueAtTime(1, t + 0.32);
        return;
      }
      // the Portimao lap's upshifts barely dip (−1.5..−3 dB, near-seamless shift): a light, quick cut
      const depth = 0.78;
      const hold = 0.045;
      g.cancelScheduledValues(t);
      g.setValueAtTime(1, t);
      g.linearRampToValueAtTime(depth, t + 0.012);
      g.setValueAtTime(depth, t + 0.012 + hold);
      g.linearRampToValueAtTime(1, t + 0.012 + hold + 0.08);
      if (this.shiftLP) {
        const f = this.shiftLP.frequency;
        const dark = 700;
        f.cancelScheduledValues(t);
        f.setValueAtTime(20000, t);
        f.exponentialRampToValueAtTime(dark, t + 0.015);
        f.setValueAtTime(dark, t + 0.015 + hold);
        f.exponentialRampToValueAtTime(20000, t + 0.015 + hold + 0.08);
      }
    }
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.status.volume = this.volume;
    this.applyGain();
  }

  /** Engine layer on/off (ENG START / STOP) — ambience, wind and one-shots keep playing. */
  /** live mix sliders (MIX experiments): true when this car runs two voices */
  get hasOverlay(): boolean { return !!this.overlayGainNode; }
  get hasSim(): boolean { return !!this.simGainNode; }
  get hasExtra(): boolean { return !!this.extraGainNode; }
  get voiceGains(): { base: number; overlay: number; sim: number; extra: number } {
    return { base: this.baseUserGain, overlay: this.overlayUserGain, sim: this.simUserGain, extra: this.extraUserGain };
  }
  setVoiceGains(base: number, overlay: number, sim = this.simUserGain, extra = this.extraUserGain): void {
    this.baseUserGain = base;
    if (this.ctx && this.baseGainNode && !this.launchBoostOn) this.baseGainNode.gain.setTargetAtTime(base, this.ctx.currentTime, 0.03);
    this.overlayUserGain = overlay;   // the gate applies it per frame
    this.simUserGain = sim;          // applied per frame like the overlay
    this.extraUserGain = extra;
  }
  /** Launch control (Marc): the AMS2 slider goes to MAX while launch is armed, back to where it was after. */
  static readonly LAUNCH_BASE_MAX = 1.5;
  get launchBoost(): boolean { return this.launchBoostOn; }
  setLaunchBoost(on: boolean): void {
    if (on === this.launchBoostOn) return;
    this.launchBoostOn = on;
    if (this.ctx && this.baseGainNode) this.baseGainNode.gain.setTargetAtTime(on ? AudioEngine.LAUNCH_BASE_MAX : this.baseUserGain, this.ctx.currentTime, on ? 0.15 : 0.4);
  }

  /** audio-thread starvation events from the sweep worklet — chop born OUTSIDE the graph */
  get sweepGlitches(): number { return this.sweep?.glitches ?? 0; }

  // RAW PLAYBACK, TWO WAYS, to split "our graph" from "that browser and that car" (Marc, Tesla, 2026-09-03:
  // the clicks scale with level on both volume controls, so they are in the signal itself). 'node' plays
  // the idle rung through ONE AudioBufferSourceNode straight into the destination: no compressor, no
  // filters, no automation. 'element' plays the same file through an HTMLAudioElement: the media
  // pipeline, no Web Audio at all. Clicks in 'element' = the file and the car. Clicks in 'node' only =
  // Web Audio's output path on that browser. Clean in both = something the full graph does.
  private rawSrc: AudioBufferSourceNode[] = [];
  private rawStep = 0;
  private rawEl: HTMLAudioElement | null = null;
  private rawTick: number | null = null;

  // THE BISECTOR. RAW 1N was clean with the whole graph running muted behind it (Marc, Tesla, v521),
  // so the clicks are made between the mixer's output and the speakers, by one of the stages the
  // mixer's own signal passes through. Each press moves the mixer's output to a different depth of
  // that chain, with everything else (the mixer's own automation, the other ladders) untouched.
  private bypassStage = 0;

  /** cycle the bypass stage; returns the line to log */
  debugBypass(): string {
    const bg = this.baseGainNode, thr = this.throttle, cut = this.shiftCut, master = this.master;
    if (!this.ctx || !bg || !thr || !cut || !master) return 'bypass: graph not ready (single-file car only)';
    this.bypassStage = (this.bypassStage + 1) % 4;
    try { bg.disconnect(); } catch { /* noop */ }
    try { thr.output.disconnect(); } catch { /* noop */ }
    switch (this.bypassStage) {
      case 1: bg.connect(this.ctx.destination); return 'bypass 1: mixer -> destination. Nothing of ours after the mixer.';
      case 2: bg.connect(master); return 'bypass 2: mixer -> master -> compressor -> destination.';
      case 3: bg.connect(cut); return 'bypass 3: mixer -> shift cut/LP, voicing, tilt, duck, engineGain, master, comp. No pedal stage.';
      default: bg.connect(thr.input); thr.output.connect(cut); return 'bypass 0: the full graph again.';
    }
  }

  /** cycle the mixers' automation bisect mode; returns the line to log */
  debugMix(): string {
    const ms = [this.mixer, this.overlayMixer, this.simMixer, this.extraMixer].filter((m): m is RPMMixer => !!m);
    if (!ms.length) return 'mix: no mixer (single-file car only)';
    const next = (((ms[0].debugMode + 1) % 4)) as 0 | 1 | 2 | 3;
    for (const m of ms) m.debugMode = next;
    return ['mix 0: normal automation.', 'mix 1: FROZEN. No gain or rate automation at all.', 'mix 2: gains move, rates (pitch) frozen.', 'mix 3: rates (pitch) move, gains frozen.'][next];
  }

  /** toggle one of the raw players; returns the line to log */
  debugRaw(mode: 'node' | 'element'): string {
    // the node player cycles: 1 source at rate 1 (clean in the Tesla, v521), 1 at 0.93 (the
    // interpolating path), 3 at rate 1 (several sources summed), 3 at 0.93, then stop
    const variants: { n: number; rate: number }[] = [{ n: 1, rate: 1 }, { n: 1, rate: 0.93 }, { n: 3, rate: 1 }, { n: 3, rate: 0.93 }];
    const nodeNext = mode === 'node' && this.rawSrc.length && this.rawStep < variants.length;
    if ((this.rawSrc.length || this.rawEl) && !nodeNext) {
      for (const s of this.rawSrc) { try { s.stop(); } catch { /* noop */ } }
      this.rawSrc = [];
      this.rawStep = 0;
      if (this.rawEl) { this.rawEl.pause(); this.rawEl.src = ''; this.rawEl = null; }
      if (this.rawTick !== null) { clearInterval(this.rawTick); this.rawTick = null; }
      if (this.engineGain) this.engineGain.gain.value = this.engineRunning ? 1 : 0;
      return 'raw: stopped';
    }
    const loop = this.mixer?.firstLoop ?? null;
    if (!loop) return 'raw: no idle loop loaded';
    const ls = loop.loopStart ?? 0;
    const le = loop.loopEnd ?? loop.buffer.duration;
    // the engine itself goes quiet so only the raw player is heard
    if (this.engineGain) this.engineGain.gain.value = 0;
    if (mode === 'node') {
      if (!this.ctx) return 'raw: no context';
      for (const s of this.rawSrc) { try { s.stop(); } catch { /* noop */ } }
      this.rawSrc = [];
      const v = variants[this.rawStep++];
      for (let i = 0; i < v.n; i++) {
        const src = this.ctx.createBufferSource();
        src.buffer = loop.buffer;
        src.loop = true; src.loopStart = ls; src.loopEnd = le;
        src.playbackRate.value = v.rate;
        src.connect(this.ctx.destination);
        src.start(0, ls + (i ? Math.random() * (le - ls - 0.1) : 0));
        this.rawSrc.push(src);
      }
      return `raw node ${this.rawStep}/${variants.length}: ${v.n} source${v.n > 1 ? 's' : ''} at rate ${v.rate} (${loop.id}) -> destination`;
    }
    const file = this.profile.samples.on[0]?.file ?? '';
    const base = this.profile.samples.basePath.replace(/\/$/, '');
    const rev = typeof __ROAR_BUILD__ !== 'undefined' ? __ROAR_BUILD__ : 'dev';
    const ext = /\.[a-z0-9]+$/i.test(file) ? '' : '.flac';
    const url = `${base}/${file}${ext}${this.grantToken ? `?g=${encodeURIComponent(this.grantToken)}&` : '?'}r=${encodeURIComponent(rev)}`;
    const el = new Audio();
    el.src = url;
    el.preload = 'auto';
    el.volume = 1;
    el.addEventListener('error', () => { /* the log line below already names the url */ });
    const play = () => { el.currentTime = ls; void el.play().catch(() => undefined); };
    el.addEventListener('canplay', play, { once: true });
    // coarse region loop: one audible jump every pass, nothing like a fast crackle
    this.rawTick = window.setInterval(() => { if (el.currentTime >= le - 0.05) el.currentTime = ls; }, 50);
    this.rawEl = el;
    return `raw element: ${url} ${ls.toFixed(2)}-${le.toFixed(2)}s`;
  }

  /** the render thread's own load: 'load avg%/peak% underrun%' or n/a where the browser cannot say */
  get renderLoadInfo(): string {
    const r = this.renderLoad;
    if (!r) return 'load n/a';
    return `load ${(r.avg * 100).toFixed(0)}%/${(r.peak * 100).toFixed(0)}% underrun ${(r.underrun * 100).toFixed(1)}%`;
  }

  /** what the context is running at: the numbers to read off a car's browser when it crackles */
  get contextInfo(): string {
    const c = this.ctx;
    if (!c) return 'no ctx';
    const ol = (c as AudioContext & { outputLatency?: number }).outputLatency;
    return `${c.sampleRate}Hz base${(c.baseLatency * 1000).toFixed(1)}ms out${ol !== undefined ? (ol * 1000).toFixed(1) + 'ms' : '?'} ${c.state} car${isCarBrowser() ? 'Y' : 'N'} ${this.mixer?.usesSampler ? 'sampler' : 'nodes'} voices${this.mixer?.voiceCount ?? 0} ${this.renderLoadInfo}`;
  }

  /** debug: record N seconds of the POST-compressor signal (exactly what reaches the speakers) */
  async debugCapture(seconds = 30): Promise<{ blob: Blob; info: string } | null> {
    if (!this.ctx || !this.comp) return null;
    const ctx = this.ctx;
    const dest = ctx.createMediaStreamDestination();
    this.comp.connect(dest);
    const rec = new MediaRecorder(dest.stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 256000 });
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => chunks.push(e.data);
    // how far the audio clock falls behind the wall clock over the take: a render thread that
    // misses quanta stalls currentTime, so a drift well past zero IS the underrun count in seconds
    const w0 = performance.now() / 1000, c0 = ctx.currentTime;
    rec.start(250);
    await new Promise((r) => setTimeout(r, seconds * 1000));
    const drift = (performance.now() / 1000 - w0) - (ctx.currentTime - c0);
    rec.stop();
    await new Promise((r) => { rec.onstop = () => r(null); });
    try { this.comp.disconnect(dest); } catch { /* ignore */ }
    const lat = (ctx as AudioContext & { outputLatency?: number }).outputLatency ?? -1;
    return { blob: new Blob(chunks, { type: 'audio/webm' }), info: `${ctx.sampleRate}Hz lat${lat.toFixed(3)} base${ctx.baseLatency?.toFixed(3)} drift${drift.toFixed(3)}s ${this.contextInfo}` };
  }

  setEngineRunning(on: boolean, tauS = 0.25): void {
    this.engineRunning = on;
    if (this.engineGain && this.ctx) this.engineGain.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, tauS);
  }

  /** Ignition one-shot; returns its duration (0 when the car has none). */
  playShot(name: 'starter' | 'startup' | 'shutoff' | 'falsestart' | 'doorOpen' | 'doorShut', gain = 1, settle?: { at: number; gain: number; over: number }): { duration: number; stop: (fadeS?: number) => void } {
    return this.shifts?.playNamed(name, gain, settle) ?? { duration: 0, stop: () => undefined };
  }

  /**
   * ENG START asks the showroom to get out of the way first, and is told how long to wait before the
   * starter so the crank lands in silence. 0 when there was nothing playing.
   */
  hushShowroom(): number {
    return this.env?.hush() ?? 0;
  }

  /** Length of an ignition one-shot in seconds (0 when the car has none). */
  shotLength(name: 'starter' | 'startup' | 'shutoff' | 'falsestart' | 'doorOpen' | 'doorShut'): number {
    return this.shifts?.lengthOf(name) ?? 0;
  }

  /** Demo paywall mute — fades the whole mix without touching the user's volume. */
  setMuted(m: boolean): void {
    if (this.muted === m) return;
    this.muted = m;
    this.applyGain(0.25);
  }

  private applyGain(tau = 0.05): void {
    if (this.master && this.ctx) {
      const g = this.muted ? 0 : this.volume * this.profile.audio.masterGain;
      this.master.gain.setTargetAtTime(g, this.ctx.currentTime, tau);
    }
  }

  async stop(): Promise<void> {
    this.mixer?.stop();
    this.synth?.stop();
    this.env?.stop();
    const ctx = this.ctx;
    this.ctx = null;
    this.mixer = null;
    this.synth = null;
    this.sweep?.stop();
    this.sweep = null;
    try { this.turboBed?.src.stop(); } catch { /* noop */ }
    this.turboBed = null; this.boost = 0;
    for (const l of this.lowStack) { try { l.src.stop(); } catch { /* noop */ } }
    this.lowStack = []; this.sweepGain = null; this.idleBed = null; this.atIdle = false;
    this.engineGain = null;
    this.shiftCut = null;
    this.shiftLP = null;
    this.rpmTilt = null;
    try { this.halfOsc?.stop(); this.fullOsc?.stop(); } catch { /* ignore */ }
    this.halfOsc = null; this.halfDepth = null; this.fullOsc = null; this.fullDepth = null;
    this.throttle = null;
    this.shifts = null;
    this.env = null;
    this.master = null;
    this.status = { ...this.status, state: 'off', report: null, progress: null };
    try {
      await ctx?.close();
    } catch {
      /* ignore */
    }
  }
}
